import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  Box,
  useMediaQuery,
} from "@mui/material";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import BedIcon from "@mui/icons-material/Bed";
import BathtubIcon from "@mui/icons-material/Bathtub";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import theme from "../theme";
import RoundedButtons from "./RoundedButtons";
import { Link, useParams, useNavigate } from "react-router-dom";
import { propertyTypes, dealTypes, coinsType } from "../reTypes";
import getLabelForValue from "../labelForValueFunction";
import { useSelector } from "react-redux";
import {
  selectPropertyId,
  selectPropertySecureNum,
  selectUid,
} from "../redux/propertyIDandSN";

export const PropertyCard = ({
  propertyId,
  propertySecureNum,
  propertyImage,
  propertyName,
  saleOrRent,
  fornitured,
  propertyPrice,
  propertySqm,
  propertyType,
  propertyAddress,
  baths,
  parking,
  rooms,
  coin,
  agentPhoneNumber,
  waze,
  managerID,
  spitimou_rid,
  uid,
}) => {
  const PropertyUid = useSelector(selectUid);
  const propertyID = useSelector(selectPropertyId);
  const propertySN = useSelector(selectPropertySecureNum);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Card
      sx={{
        width: isMobile ? "90vw" : "30vw",
        borderRadius: "15px",
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: 480 }, // <<< FIXED DESKTOP HEIGHT
        overflow: "hidden",
        "&:hover": {
          cursor: "pointer",
          transform: "scale(1.02)",
          transition: "transform 0.3s ease-in-out",
        },
      }}
      elevation={4}
    >
      {/* IMAGE HEADER */}
      <Box sx={{ position: "relative" }}>
        <Link
          to={`/${propertySecureNum}/${propertyId}/${uid}`}
          style={{ textDecoration: "none" }}
        >
          <CardMedia
            component="img"
            height="180"
            image={propertyImage}
            alt={propertyName}
            sx={{ objectFit: "cover" }}
          />
        </Link>

        {/* BADGES */}
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: 10,
            display: "flex",
            gap: 1,
            zIndex: 2,
          }}
        >
          {saleOrRent !== "0" && (
            <Box
              sx={{
                backgroundColor: "#edce07",
                opacity: 0.85,
                borderRadius: "5px",
                p: "3px 8px",
                fontWeight: "bold",
              }}
            >
              {getLabelForValue(saleOrRent, dealTypes)}
            </Box>
          )}
          {fornitured && (
            <Box
              sx={{
                backgroundColor: "black",
                opacity: 0.7,
                borderRadius: "5px",
                p: "3px 8px",
                color: "white",
                fontSize: "14px",
              }}
            >
              מרוהט
            </Box>
          )}
          {/* {spitimou_rid && spitimou_rid !== "0" && (
            <Box
              sx={{
                backgroundColor: "green",
                opacity: 0.7,
                borderRadius: "5px",
                p: "3px 8px",
                color: "white",
                fontSize: "14px",
              }}
            >
              in spitimou
            </Box>
          )} */}
        </Box>

        {/* PHONE + WAZE */}
        <Box
          sx={{
            position: "absolute",
            bottom: -20,
            left: 10,
            zIndex: 3,
            display: "flex",
          }}
        >
          <RoundedButtons
            agentPhoneNumber={agentPhoneNumber}
            waze={waze}
            uid={uid}
            propertyId={propertyId}
            propertySecureNum={propertySecureNum}
            propertyName={propertyName}
          />
        </Box>
      </Box>

      {/* CONTENT */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          pt: 3,
          textAlign: "right", // <<< ALL TEXT RIGHT ALIGNED
        }}
      >
        {/* <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            // px: 1, // <<< ONE consistent padding
            textAlign: "right", // <<< right-align everything inside
            // gap: 0.5, // <<< consistent professional spacing
          }}
        > */}
        {/* TITLE (3 lines MAX, consistent height) */}
        <Box
          sx={{
            minHeight: 72, // <<< FIXED HEIGHT FOR TITLE AREA (Desktop)
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              // DESKTOP (md and up) → 3-line clamp
              display: { xs: "block", md: "-webkit-box" },
              WebkitLineClamp: { md: 3 },
              WebkitBoxOrient: "vertical",

              // MOBILE → allow full height
              overflow: { xs: "visible", md: "hidden" },
              textOverflow: { xs: "unset", md: "ellipsis" },

              lineHeight: 1.3,
              // fontWeight: 600,
              textAlign: "right",
              whiteSpace: { xs: "normal", md: "unset" }, // Mobile wraps fully\
            }}
          >
            {propertyName}
          </Typography>
        </Box>

        {/* PRICE + TYPE */}
        <Box sx={{ mb: 1 }}>
          {propertyPrice && (
            <Typography
              variant="h6"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                // fontWeight: "bold",
                textAlign: "right",
              }}
            >
              {getLabelForValue(coin, coinsType)}
              {Number(propertyPrice).toLocaleString()}
            </Typography>
          )}
          {propertyType && (
            <Typography variant="body1" sx={{ textAlign: "right", mt: 1 }}>
              {getLabelForValue(propertyType, propertyTypes)}
            </Typography>
          )}
        </Box>

        {/* ADDRESS */}
        {propertyAddress && (
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mb: 2,
              color: "text.secondary",
              textAlign: "right",
            }}
          >
            {propertyAddress}
          </Typography>
        )}
        {/* </Box> */}

        {/* ICON ROW */}
        <Grid
          container
          justifyContent="space-between"
          sx={{ mt: "auto", pt: 2, flexDirection: "row-reverse" }}
        >
          <Grid item xs={3} textAlign="center">
            <BedIcon color="primary" />
            <div>{rooms} חדרים</div>
          </Grid>

          <Grid item xs={3} textAlign="center">
            <BathtubIcon color="primary" />
            <div>{baths} מקלחות</div>
          </Grid>

          <Grid item xs={3} textAlign="center">
            <DirectionsCarIcon color="primary" />
            <div>{parking} חניות</div>
          </Grid>

          <Grid item xs={3} textAlign="center">
            <SquareFootIcon color="primary" />
            <div>{propertySqm} מ"ר</div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
